import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Tag, Plus, Pencil, X, RefreshCw, Copy, List, Table, Save,
  Search, GripVertical
} from 'lucide-react';
import { useDatabase } from '../../context/DatabaseContext';
import { useNotifications } from '../../context/NotificationContext';
import { integrationConfigApi } from '../../services/api';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';

// ─── Field definitions ────────────────────────────────────────────────────────

const FIELDS = [
  { key: 'plcDatatype',    label: 'PLC Datatype',    required: true,  width: 140, span: 8  },
  { key: 'plcDatamember',  label: 'PLC Datamember',  required: true,  width: 160, span: 8  },
  { key: 'datamemberDesc', label: 'Description',     required: false, width: 220, span: 8  },
  { key: 'tool2Custom',    label: 'Tool2 Custom',    required: false, width: 140, span: 8  },
  { key: 'access',         label: 'Access',          required: false, width: 80,  span: 4  },
  { key: 'ptType',         label: 'PT Type',         required: false, width: 80,  span: 4  },
  { key: 'almClass',       label: 'Alm Class',       required: false, width: 90,  span: 4  },
  { key: 'almEnable',      label: 'Alm Enable',      required: false, width: 90,  span: 4  },
  { key: 'almHigh1',       label: 'Alm High 1',      required: false, width: 90,  span: 4  },
  { key: 'almHigh2',       label: 'Alm High 2',      required: false, width: 90,  span: 4  },
  { key: 'almLow1',        label: 'Alm Low 1',       required: false, width: 90,  span: 4  },
  { key: 'almLow2',        label: 'Alm Low 2',       required: false, width: 90,  span: 4  },
  { key: 'almStr',         label: 'Alm Str',         required: false, width: 80,  span: 4  },
  { key: 'dispLimHigh',    label: 'Disp Lim High',   required: false, width: 100, span: 4  },
  { key: 'dispLimLow',     label: 'Disp Lim Low',    required: false, width: 100, span: 4  },
  { key: 'setpointHigh',   label: 'Setpoint High',   required: false, width: 100, span: 4  },
  { key: 'setpointLow',    label: 'Setpoint Low',    required: false, width: 100, span: 4  },
  { key: 'dataLog',        label: 'Data Log',        required: false, width: 80,  span: 4  },
  { key: 'alarmConfig',    label: 'Alarm Config',    required: false, width: 120, span: 8  },
];

const GRID_COLUMNS = FIELDS.map(f => ({ key: f.key, fieldName: f.key, label: f.label, width: f.width }));
const EMPTY_FORM   = Object.fromEntries(FIELDS.map(f => [f.key, '']));
const toSaveDto    = (form) => Object.fromEntries(FIELDS.map(f => [f.key, form[f.key] || null]));
const fromRow      = (row)  => Object.fromEntries(FIELDS.map(f => [f.key, row[f.key] ?? '']));

// ─── Detail form — used in list view and add-new modal ────────────────────────

const DetailForm = ({ form, setForm, readOnly }) => {
  const field = (f) => (
    <div key={f.key} className={`form-group col-span-${f.span}`}>
      <label>{f.label}{f.required && <span style={{ color: 'var(--color-error)', marginLeft: '2px' }}>*</span>}</label>
      <input
        className="form-input"
        value={form[f.key]}
        onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
        readOnly={readOnly}
      />
    </div>
  );
  return (
    <div className="detail-grid-24">
      <div className="col-span-24"><div className="detail-section-header">Identification</div></div>
      {FIELDS.slice(0, 3).map(field)}
      <div className="col-span-24"><div className="detail-section-header">Tag Configuration</div></div>
      {FIELDS.slice(3, 6).map(field)}
      <div className="col-span-24"><div className="detail-section-header">Alarm Configuration</div></div>
      {FIELDS.slice(6, 14).map(field)}
      <div className="col-span-24"><div className="detail-section-header">Display &amp; Logging</div></div>
      {FIELDS.slice(14).map(field)}
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const PlcTagXRefPage = () => {
  const { dbSlug: slug } = useDatabase();
  const { notify } = useNotifications();

  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [viewMode, setViewMode] = useState('list');

  // Search + action menu
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen]     = useState(false);
  const menuRef = useRef(null);

  // List view state
  const [selected, setSelected] = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [isNew, setIsNew]       = useState(false);
  const [editing, setEditing]   = useState(false);
  const [saving, setSaving]     = useState(false);

  // Datasheet inline edit state
  const [editModeEnabled, setEditModeEnabled]     = useState(false);
  const [editedData, setEditedData]               = useState({});
  const [isDatasheetSaving, setIsDatasheetSaving] = useState(false);
  const originalDataRef = useRef([]);

  // Datasheet filters
  const [filters, setFilters] = useState({});

  // Add-new modal (datasheet mode only)
  const [showAddModal, setShowAddModal]     = useState(false);
  const [addForm, setAddForm]               = useState(EMPTY_FORM);
  const [addModalSaving, setAddModalSaving] = useState(false);

  // Copy-from modal
  const [showCopy, setShowCopy]             = useState(false);
  const [copySlugs, setCopySlugs]           = useState([]);
  const [copySourceSlug, setCopySourceSlug] = useState('');
  const [copyRows, setCopyRows]             = useState([]);
  const [copySelected, setCopySelected]     = useState([]);
  const [copySaving, setCopySaving]         = useState(false);

  // ── Click-outside for action menu ────────────────────────────────────────────

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Load ─────────────────────────────────────────────────────────────────────

  const loadRows = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const res = await integrationConfigApi.getTagXRef(slug);
      setRows(res.data);
    } catch {
      notify.error('Failed to load tag xref');
    } finally {
      setLoading(false);
    }
  }, [slug, notify]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // ── List view ─────────────────────────────────────────────────────────────────

  const handleSelect = (row) => {
    setSelected(row);
    setForm(fromRow(row));
    setEditing(false);
    setIsNew(false);
  };

  const handleNew = () => {
    setSelected(null);
    setForm(EMPTY_FORM);
    setIsNew(true);
    setEditing(true);
  };

  const handleCancel = () => {
    if (selected) { setForm(fromRow(selected)); setEditing(false); setIsNew(false); }
    else { setIsNew(false); setEditing(false); setForm(EMPTY_FORM); }
  };

  const handleSave = async () => {
    if (!form.plcDatatype?.trim() || !form.plcDatamember?.trim()) {
      notify.warning('PLC Datatype and Datamember are required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const res = await integrationConfigApi.addTagXRef(slug, toSaveDto(form));
        setRows(prev => [...prev, res.data]);
        setSelected(res.data);
        setForm(fromRow(res.data));
        setIsNew(false);
        setEditing(false);
        notify.success('Row added');
      } else {
        const res = await integrationConfigApi.updateTagXRef(slug, selected.id, toSaveDto(form));
        setRows(prev => prev.map(r => r.id === selected.id ? res.data : r));
        setSelected(res.data);
        setForm(fromRow(res.data));
        setEditing(false);
        notify.success('Row updated');
      }
    } catch {
      notify.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (target) => {
    if (!target) return;
    if (!window.confirm(`Delete ${target.plcDatatype}.${target.plcDatamember}?`)) return;
    try {
      await integrationConfigApi.deleteTagXRef(slug, target.id);
      setRows(prev => prev.filter(r => r.id !== target.id));
      if (selected?.id === target.id) { setSelected(null); setForm(EMPTY_FORM); setEditing(false); }
      notify.success('Row deleted');
    } catch {
      notify.error('Delete failed');
    }
  };

  // ── Datasheet inline editing ───────────────────────────────────────────────

  const enterEditMode = () => {
    originalDataRef.current = [...rows];
    setEditedData({});
    setEditModeEnabled(true);
  };

  const handleDatasheetCancel = () => {
    setEditedData({});
    setEditModeEnabled(false);
  };

  const handleCellChange = useCallback((rowId, fieldKey, value) => {
    setEditedData(prev => {
      const rowEdits = prev[rowId] || {};
      const originalRow = originalDataRef.current.find(r => r.id === rowId);
      const originalValue = originalRow?.[fieldKey] ?? '';
      const normalized = value ?? '';
      const normalizedOrig = originalValue === null || originalValue === undefined ? '' : String(originalValue);

      if (normalized === normalizedOrig) {
        const { [fieldKey]: _, ...remaining } = rowEdits;
        if (Object.keys(remaining).length === 0) {
          const { [rowId]: __, ...remainingRows } = prev;
          return remainingRows;
        }
        return { ...prev, [rowId]: remaining };
      }
      return { ...prev, [rowId]: { ...rowEdits, [fieldKey]: value } };
    });
  }, []);

  const handleDatasheetSave = async () => {
    const dirtyRowIds = Object.keys(editedData).map(Number);
    if (!dirtyRowIds.length) { setEditModeEnabled(false); return; }
    setIsDatasheetSaving(true);
    try {
      await Promise.all(dirtyRowIds.map(rowId => {
        const original = originalDataRef.current.find(r => r.id === rowId);
        const changes = editedData[rowId];
        const merged = { ...fromRow(original), ...changes };
        return integrationConfigApi.updateTagXRef(slug, rowId, toSaveDto(merged));
      }));
      await loadRows();
      setEditedData({});
      setEditModeEnabled(false);
      notify.success(`Saved ${dirtyRowIds.length} row(s)`);
    } catch {
      notify.error('Save failed');
    } finally {
      setIsDatasheetSaving(false);
    }
  };

  const renderEditableCell = useCallback((row, col, value, isEdited) => {
    if (isDatasheetSaving) return <span>{value ?? ''}</span>;
    return (
      <input
        type="text"
        value={value ?? ''}
        onChange={e => handleCellChange(row.id, col.key, e.target.value)}
        style={{
          width: '100%',
          border: 'none',
          background: 'transparent',
          padding: '0 2px',
          fontSize: 'inherit',
          fontFamily: 'inherit',
          color: 'inherit',
          outline: 'none',
        }}
      />
    );
  }, [isDatasheetSaving, handleCellChange]);

  // ── Add-new modal (datasheet mode) ────────────────────────────────────────────

  const openAddModal = () => {
    setAddForm(EMPTY_FORM);
    setShowAddModal(true);
  };

  const handleAddModalSave = async () => {
    if (!addForm.plcDatatype?.trim() || !addForm.plcDatamember?.trim()) {
      notify.warning('PLC Datatype and Datamember are required');
      return;
    }
    setAddModalSaving(true);
    try {
      const res = await integrationConfigApi.addTagXRef(slug, toSaveDto(addForm));
      setRows(prev => [...prev, res.data]);
      setShowAddModal(false);
      notify.success('Row added');
    } catch {
      notify.error('Save failed');
    } finally {
      setAddModalSaving(false);
    }
  };

  // ── Copy-from ─────────────────────────────────────────────────────────────────

  const openCopyModal = async () => {
    try {
      const res = await integrationConfigApi.getSlugs();
      setCopySlugs(res.data.filter(s => s !== slug));
      setCopySourceSlug('');
      setCopyRows([]);
      setCopySelected([]);
      setShowCopy(true);
    } catch {
      notify.error('Failed to load slugs');
    }
  };

  const handleCopySlugChange = async (sourceSlug) => {
    setCopySourceSlug(sourceSlug);
    setCopyRows([]);
    setCopySelected([]);
    if (!sourceSlug) return;
    try {
      const res = await integrationConfigApi.getTagXRefFrom(slug, sourceSlug);
      setCopyRows(res.data);
    } catch {
      notify.error('Failed to load source rows');
    }
  };

  const handleExecuteCopy = async () => {
    if (!copySelected.length) return;
    const xRefIds = copyRows.filter(r => copySelected.includes(r.plcDatatype)).map(r => r.id);
    setCopySaving(true);
    try {
      await integrationConfigApi.copyTagXRef(slug, { sourceSlug: copySourceSlug, xRefIds });
      await loadRows();
      setShowCopy(false);
      notify.success(`Copied ${copySelected.length} datatype(s) — ${xRefIds.length} row(s)`);
    } catch {
      notify.error('Copy failed');
    } finally {
      setCopySaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  const q = searchTerm.toLowerCase();
  const filteredRows = q
    ? rows.filter(r =>
        r.plcDatatype?.toLowerCase().includes(q) ||
        r.plcDatamember?.toLowerCase().includes(q) ||
        r.datamemberDesc?.toLowerCase().includes(q)
      )
    : rows;

  return (
    <div className="module-container">

      {/* HEADER */}
      <div className="module-header">
        <div className="module-header-left">
          <div className="module-header-tabs">
            <button onClick={() => { setViewMode('list'); handleDatasheetCancel(); }} className={`tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`} title="List View"><List size={18} /></button>
            <button onClick={() => setViewMode('datasheet')} className={`tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`} title="Datasheet View"><Table size={18} /></button>
          </div>
        </div>
        <div className="module-header-actions">

          {/* CRUD — list view */}
          {viewMode === 'list' && (
            <div className="module-header-crud">
              <button className="crud-btn" onClick={handleNew} title="Add New Record"><Plus size={18} /></button>
              <button onClick={editing ? handleCancel : () => setEditing(true)} className={`crud-btn ${!editing && !selected ? 'disabled' : ''}`} disabled={!editing && !selected} title={editing ? 'Cancel' : 'Edit Record'}>
                {editing ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`crud-btn ${!editing ? 'disabled' : ''}`} onClick={handleSave} disabled={!editing || saving} title="Save Changes"><Save size={18} /></button>
            </div>
          )}

          {/* CRUD — datasheet view */}
          {viewMode === 'datasheet' && !editModeEnabled && (
            <div className="module-header-crud">
              <button className="crud-btn" onClick={openAddModal} title="Add New Record"><Plus size={18} /></button>
              <button className="crud-btn" onClick={enterEditMode} title="Edit Inline"><Pencil size={18} /></button>
            </div>
          )}
          {viewMode === 'datasheet' && editModeEnabled && (
            <div className="module-header-crud">
              <button className="crud-btn" onClick={handleDatasheetSave} disabled={isDatasheetSaving} title="Save Changes"><Save size={18} /></button>
              <button className="crud-btn" onClick={handleDatasheetCancel} disabled={isDatasheetSaving} title="Cancel"><X size={18} /></button>
            </div>
          )}

          {/* Search */}
          <div className="search-wrapper">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="search-input"
            />
          </div>

          {/* Action menu (6-dot) */}
          <div className="action-menu" ref={menuRef}>
            <button className="action-menu-btn" onClick={() => setMenuOpen(o => !o)} title="Actions">
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="action-menu-dropdown">
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); openCopyModal(); }}>
                  <Copy size={16} /><span>Copy from another database</span>
                </button>
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); loadRows(); }}>
                  <RefreshCw size={16} /><span>Refresh data</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <div className="list-view">
          <div className="list-panel">
            <div className="list-panel-header">
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-gray-600)' }}>
                {filteredRows.length}{filteredRows.length !== rows.length ? `/${rows.length}` : ''} row{rows.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="list-scroll">
              {loading ? (
                <div className="task-list-empty">Loading...</div>
              ) : filteredRows.length === 0 ? (
                <div className="task-list-empty">{rows.length === 0 ? 'No rows — click + to add' : 'No matches'}</div>
              ) : filteredRows.map(r => (
                <button key={r.id} onClick={() => handleSelect(r)} className={`list-item-btn ${selected?.id === r.id ? 'selected' : ''}`}>
                  <span className="list-item-primary">{r.plcDatatype}</span>
                  <span className="list-item-secondary">{r.plcDatamember}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="detail-panel">
            {(selected || isNew) ? (
              <div className="detail-content" style={{ overflow: 'auto', height: '100%' }}>
                <DetailForm form={form} setForm={setForm} readOnly={!editing} />
                {selected && editing && (
                  <div style={{ padding: '8px 0' }}>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(selected)}>Delete</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="detail-panel-empty">
                <Tag size={32} className="detail-panel-empty-icon" />
                <p>Select a row or click + to create one</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* DATASHEET VIEW */}
      {viewMode === 'datasheet' && (
        <div className="datasheet-view">
          <FilterableDataGrid
            data={filteredRows}
            allData={rows}
            columns={GRID_COLUMNS}
            filters={filters}
            setFilters={setFilters}
            columnWidths={{}}
            editModeEnabled={editModeEnabled}
            editedData={editedData}
            renderEditableCell={renderEditableCell}
          />
        </div>
      )}

      {/* ADD-NEW MODAL (datasheet mode) */}
      {showAddModal && (
        <div className="sprint-add-task-overlay" onClick={() => setShowAddModal(false)}>
          <div className="sprint-add-task-modal" style={{ maxWidth: '720px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Tag XRef</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
              <DetailForm form={addForm} setForm={setAddForm} readOnly={false} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddModalSave} disabled={addModalSaving}>
                {addModalSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* COPY-FROM MODAL */}
      {showCopy && (
        <div className="sprint-add-task-overlay" onClick={() => setShowCopy(false)}>
          <div className="sprint-add-task-modal" style={{ maxWidth: '600px', width: '100%' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Copy Tag XRef From Another Database</h3>
              <button className="modal-close" onClick={() => setShowCopy(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Source Database</label>
                <select className="form-input" value={copySourceSlug} onChange={e => handleCopySlugChange(e.target.value)}>
                  <option value="">— Select —</option>
                  {copySlugs.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {copyRows.length > 0 && (() => {
                const distinctTypes = [...new Set(copyRows.map(r => r.plcDatatype))].sort();
                const memberCount = copySelected.reduce((n, dt) => n + copyRows.filter(r => r.plcDatatype === dt).length, 0);
                return (
                  <div>
                    <label style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '6px', display: 'block' }}>
                      Select datatypes to copy (all datamembers included):
                    </label>
                    <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: '4px' }}>
                      {distinctTypes.map(dt => {
                        const count = copyRows.filter(r => r.plcDatatype === dt).length;
                        return (
                          <label key={dt} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', cursor: 'pointer', borderBottom: '1px solid var(--color-border-light)' }}>
                            <input type="checkbox" checked={copySelected.includes(dt)} onChange={e => setCopySelected(prev => e.target.checked ? [...prev, dt] : prev.filter(d => d !== dt))} style={{ width: '1rem', height: '1rem' }} />
                            <span style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: 'var(--text-xs)' }}>{dt}</span>
                            <span style={{ color: 'var(--color-gray-500)', fontSize: 'var(--text-xs)' }}>{count} member{count !== 1 ? 's' : ''}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                      <button className="btn btn-secondary" style={{ fontSize: 'var(--text-xs)', padding: '4px 8px' }} onClick={() => setCopySelected(distinctTypes)}>Select All</button>
                      <button className="btn btn-secondary" style={{ fontSize: 'var(--text-xs)', padding: '4px 8px' }} onClick={() => setCopySelected([])}>Clear</button>
                    </div>
                    {memberCount > 0 && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', marginTop: '6px' }}>{memberCount} total row{memberCount !== 1 ? 's' : ''} will be copied</p>}
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCopy(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleExecuteCopy} disabled={!copySelected.length || copySaving}>
                {copySaving ? 'Copying...' : `Copy ${copySelected.length || ''} Datatype${copySelected.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PlcTagXRefPage;
